import {
  Filter,
  HelpCircle,
  Inbox,
  Layers,
  MessageSquare,
  Reply,
  Rocket,
  Workflow,
} from 'lucide-react';
import type { GuideSection } from '@/modules/chatbot/guideContent';

/**
 * Structured guide content for the Conversations module.
 *
 * Mirrors the shape of `chatbot/guideContent.ts` — re-uses the same
 * `GuideSection` / `GuideBlock` types (imported from the chatbot module) so
 * the renderer in `ConversationsGuidePage` shares its block vocabulary with
 * `ChatbotGuidePage`. Keeping the types co-owned by the chatbot module
 * avoids parallel/diverging type drift between the two guides.
 */

export const CONVERSATIONS_GUIDE_SECTIONS: GuideSection[] = [
  // ── Section 1 ────────────────────────────────────────────────────────────
  {
    id: 'what-it-is',
    title: 'What Conversations is',
    icon: Inbox,
    intro:
      'Conversations is your unified inbox — every chat with every customer across every channel, on one timeline per customer. WhatsApp messages, portal-widget chats, emails, the bot’s exchanges — all land here in one place.',
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'Every customer interaction is logged.',
            text:
              "A customer messaging via the portal widget, an email coming in, the bot answering a question — each one creates or appends to a conversation thread.",
          },
          {
            label: 'One thread per (customer, channel).',
            text:
              'A customer who emails you today and emails you again next week sees the same email thread continuing. Their portal chat is a separate thread.',
          },
          {
            label: 'The bot writes here too.',
            text:
              "When the customer-facing bot answers a question on the portal widget, those turns appear in the conversation just like a human agent’s would (the bot’s messages are marked with a small bot icon).",
          },
          {
            label: "It's a viewer today.",
            text:
              'You can browse, search, and triage from here. Replying / resolving / reassigning happen on the matching ticket (Support queue) for now — more on that below.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'important',
        title: 'Important',
        text:
          'This page is read-only today. Replies, internal notes, reassign, and convert-to-ticket are all on the roadmap. For now, agents read here and act in the Support queue.',
      },
    ],
  },

  // ── Section 2 ────────────────────────────────────────────────────────────
  {
    id: 'how-created',
    title: 'How conversations are created',
    icon: Layers,
    intro:
      "You don't create conversations — your customers do. Each channel has its own way of starting one.",
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'Email',
            text:
              '— A customer emails your support address (configured via Channels). The runtime polls Gmail every 60 seconds; each new email auto-creates a conversation thread (and a linked support ticket with an auto-respond). Customer’s reply on the same email thread appends to the same conversation.',
          },
          {
            label: 'Portal / chat widget',
            text:
              '— When a known customer chats with the bot on the portal, the bot’s pipeline persists each turn (customer message in, bot reply out) to a conversation. The widget restores the last 20 messages on the customer’s next open, so they pick up where they left off.',
          },
          {
            label: 'WhatsApp',
            text:
              '— Roadmap. When the WhatsApp adapter ships, a customer messaging your WhatsApp Business number will create or extend their WhatsApp conversation here.',
          },
          {
            label: 'Phone / SMS',
            text: '— Future. Not on the near roadmap.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Tip',
        text:
          'A customer can have multiple conversations — one per channel they use. The Customer profile page shows all their conversations across channels in one view.',
      },
    ],
    cta: { label: 'Open Channels setup', href: '/channels' },
  },

  // ── Section 3 ────────────────────────────────────────────────────────────
  {
    id: 'triage',
    title: 'Triaging the inbox',
    icon: Filter,
    intro:
      'When the inbox grows, the same triage moves work regardless of channel.',
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'Filter by channel',
            text:
              '— quick chips at the top of the inbox. Useful when you only want to focus on emails this morning, or portal chats only.',
          },
          {
            label: 'Search',
            text:
              '— by customer name, subject, or any text in a recent message snippet.',
          },
          {
            label: 'Unread dot',
            text:
              '— a small dot next to a customer’s name means "new since last viewed". Today this is set by the runtime when an inbound message lands.',
          },
          {
            label: 'Status badge',
            text:
              '— Open, Awaiting customer, Awaiting agent, Resolved, Archived. Mostly informational right now; the Support queue’s status is the one to act on for email-originated threads.',
          },
          {
            label: 'Last-message time',
            text:
              '— relative time so the most-recent threads naturally float to the top of the list.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Tip',
        text:
          'A customer with multiple unanswered messages across different channels probably needs a single human follow-up, not multiple. Use the Customer profile page (link inside the conversation detail) to see all their threads side-by-side.',
      },
    ],
  },

  // ── Section 4 ────────────────────────────────────────────────────────────
  {
    id: 'thread-anatomy',
    title: "What you'll see in a thread",
    icon: MessageSquare,
    intro:
      'Each conversation has a timeline of messages with simple visual cues.',
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'Inbound (from the customer)',
            text: '— left-aligned, neutral background.',
          },
          {
            label: 'Outbound (from your side)',
            text: '— right-aligned, accent background. Subdivided:',
          },
          {
            label: 'Agent reply',
            text:
              '— primary tone. Sent by a human via the Support reply composer.',
          },
          {
            label: 'Bot reply',
            text:
              '— info-toned with a small bot icon. The customer-facing chatbot answered them automatically (using your approved knowledge — see the Chatbot guide).',
          },
          {
            label: 'Auto-respond',
            text:
              '— muted tone with an "Auto" chip. The auto-acknowledgement that the email channel sends when a new email creates a ticket. No human is on the other end of this yet — it’s the system saying "we got your message".',
          },
          {
            label: 'Internal note',
            text:
              '— Roadmap. When implemented, will be visible to the team only, never shared with the customer.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Note',
        text:
          'The bot’s own replies are tagged in the timeline so you can immediately see whether the customer has been spoken to by a human yet, or only by the bot.',
      },
    ],
  },

  // ── Section 5 ────────────────────────────────────────────────────────────
  {
    id: 'replying',
    title: 'Replying to a customer',
    icon: Reply,
    intro:
      "Today, agent replies happen on the matching ticket in the Support queue, not on this page. Here's the workflow.",
    body: [
      {
        kind: 'numbered',
        items: [
          {
            text:
              "Open the conversation in the inbox and read the customer’s message + the bot’s auto-respond (if any).",
          },
          {
            text: 'Note the customer name and the channel.',
          },
          {
            text: 'Go to Support queue (/support).',
          },
          {
            text:
              'Find the ticket for this customer — sorting by most-recent helps; the ticket reference (SR-XXXXXX) is also in the auto-respond subject line if you want to search for it.',
          },
          {
            text:
              'Open that ticket → use the Conversation reply composer at the bottom. Your reply goes out via the same channel the customer used (email → Gmail send; WhatsApp + others → when adapters ship).',
          },
          {
            text:
              'Your reply will also appear back in this Conversations inbox — same thread, marked as your outbound message.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'important',
        title: 'Important',
        text:
          "When you reply from the Support ticket, the conversation here updates automatically. They're two views of the same data — the conversation thread and the support case are linked at the data layer.",
      },
    ],
    cta: { label: 'Open Support queue', href: '/support' },
  },

  // ── Section 6 ────────────────────────────────────────────────────────────
  {
    id: 'relationships',
    title: 'How this fits with Chatbot and Channels',
    icon: Workflow,
    intro:
      'Conversations is the read-side of customer engagement. Chatbot and Channels are the input and transport sides.',
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'Channels',
            text:
              '(Email today; WhatsApp / SMS / etc. on the roadmap) deliver inbound messages into Conversations and send outbound messages from Support.',
          },
          {
            label: 'Chatbot',
            text:
              'writes its own turns into Conversations whenever the customer chats with it on the portal — so the bot’s history is part of the same unified timeline.',
          },
          {
            label: 'Support',
            text:
              'is where you act on a conversation — reply, resolve, raise tickets, escalate. It’s also where AI-drafted knowledge entries (Loop B) come from — resolving a support case auto-drafts a knowledge entry for the bot to use next time.',
          },
          {
            label: 'Customers',
            text:
              'is where you see one customer’s whole history across all of the above in one view.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Note',
        text:
          'A common workflow: customer emails → channels polls Gmail → Conversations gets the new thread + a ticket appears in Support → you reply from Support → customer sees your reply on the same email thread → their reply shows back here. The cycle is: Channels (in) → Conversations (view) → Support (act) → Channels (out).',
      },
    ],
  },

  // ── Section 7 ────────────────────────────────────────────────────────────
  {
    id: 'roadmap',
    title: "What's coming next",
    icon: Rocket,
    intro:
      "The honest list of what isn't built yet on this page. Tracked in the BACKLOG.md Channels epic.",
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'In-page reply composer',
            text:
              '— the right-hand "Need to reply?" card will become a real composer once we’ve decided whether the canonical reply experience lives here or stays on the Support ticket. Likely both views will let you reply.',
          },
          {
            label: 'Mark resolved / reassign',
            text:
              '— needs the team-routing model. Today there’s a single Support queue (per the product positioning); a team / assignee model is a later add.',
          },
          {
            label: 'Convert to ticket',
            text:
              '— for portal/WhatsApp conversations the agent decides should be tracked as a formal case. Email-originated threads already have a linked ticket automatically.',
          },
          {
            label: 'Internal notes',
            text: '— team-only sidebar notes on a thread.',
          },
          {
            label: 'WhatsApp + SMS + phone channels',
            text: '— adapters in the Channels epic.',
          },
          {
            label: 'Unread filter',
            text: '— a quick "show me only unread" chip on the inbox.',
          },
          {
            label: 'Linked-ticket badge',
            text:
              '— show a small "Linked to SR-XXXXXX" pill on each inbox row so the agent can jump straight to the ticket from the inbox.',
          },
        ],
      },
    ],
  },

  // ── Section 8 ────────────────────────────────────────────────────────────
  {
    id: 'faq',
    title: 'Frequently asked questions',
    icon: HelpCircle,
    intro: 'Common owner / agent questions about the Conversations module.',
    body: [
      {
        kind: 'qa',
        items: [
          {
            question: 'Where do the messages come from?',
            answer:
              'From whichever channel the customer used. Today, mostly Email (via Gmail OAuth — see Channels) and Portal (the embedded bot widget). WhatsApp / phone are roadmap.',
          },
          {
            question: "Why can't I reply here?",
            answer:
              'This page is read-only today. We chose to ship reading first so you can see everything in one place; the in-page composer is on the roadmap. For now, replies happen via the matching ticket in the Support queue, which is wired end-to-end for Email channels.',
          },
          {
            question:
              "What's the difference between a Conversation and a Support Request (ticket)?",
            answer:
              'A Conversation is the message thread — every back-and-forth on a channel. A Support Request is the case — something to track, prioritise, resolve. For email, every new conversation automatically gets a linked ticket. For bot escalations, the ticket is created the moment the bot escalates. Some conversations never become tickets (the customer just chatted to the bot, no escalation needed).',
          },
          {
            question:
              "The bot's replies are on the timeline — does that mean the bot is sending things \"on my behalf\"?",
            answer:
              "Yes — when the customer-facing chatbot answers a question on the portal, the bot’s reply is logged as an outbound message just like a human agent’s. It's marked with a bot icon so you can tell them apart. The bot only ever answers from approved knowledge — see the Chatbot guide for the full picture.",
          },
          {
            question:
              "Can I see all of one customer's conversations across channels?",
            answer:
              'Yes — open the Customer profile (from the link inside any conversation detail). It shows their full Engage history including all channel threads, support requests, tickets, and orders.',
          },
          {
            question: 'How long is conversation history kept?',
            answer:
              'Indefinitely for now. A formal retention policy (e.g. 12 months) is on the backlog — see BACKLOG.md §A.',
          },
          {
            question: 'Are conversations isolated per tenant?',
            answer:
              "Yes — every query is tenantId-scoped. There's no way to see another tenant’s conversations from this app (or from the API). See CHATBOT_ARCHITECTURE.md §5 for the full isolation model.",
          },
        ],
      },
    ],
  },
];

// Re-export the shared types so consumers of this file (just the page for now)
// can import everything from one place if they prefer.
export type {
  CalloutTone,
  GuideBlock,
  GuideSection,
} from '@/modules/chatbot/guideContent';

/**
 * Section count is exported for parity with the chatbot guide — useful if
 * the parent page ever wants to show a "X sections" pill.
 */
export const CONVERSATIONS_GUIDE_SECTION_COUNT =
  CONVERSATIONS_GUIDE_SECTIONS.length;

// Anchor IDs used inside the page — declared here for IDE autocomplete and
// to make grep-friendly cross-references possible from sibling modules.
export const CONVERSATIONS_GUIDE_SECTION_IDS = [
  'what-it-is',
  'how-created',
  'triage',
  'thread-anatomy',
  'replying',
  'relationships',
  'roadmap',
  'faq',
] as const;

export type ConversationsGuideSectionId =
  (typeof CONVERSATIONS_GUIDE_SECTION_IDS)[number];
